import React, {
	useState,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type ReactNode,
	type MouseEvent,
	useCallback,
} from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import DropdownMenu, {
	DropdownItemGroup,
	type CustomTriggerProps,
	DropdownItem,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { FORGE_ENTITY_TYPE } from '@atlassian/jira-issue-view-common-constants';
import { ellipsis } from '@atlassian/jira-issue-view-common-styles';
import type { ActivityItem } from '@atlassian/jira-issue-view-common-types/src/activity-item-type';
import {
	ForgeIssueActivityTrigger,
	ForgeIssueActivityItem,
} from '@atlassian/jira-issue-view-forge-issue-activity/src';
import {
	AnalyticsEventToProps,
	ContextualAnalyticsData,
	DROPDOWN,
	fireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	MountEvent,
} from '@atlassian/jira-product-analytics-bridge';
import type { SelectActivityItem } from '../../common/ui/types';

type Props = {
	items: ActivityItem[];
	selectedItem: ActivityItem;
	onSelectActivityItem: SelectActivityItem;
};

// eslint-disable-next-line jira/styled/styled-component-order, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledDropdownItem = styled(DropdownItem)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${16 * gridSize}px`,
});

StyledDropdownItem.displayName = 'DropdownItem';

const DropdownItemWithAnalytics = AnalyticsEventToProps('dropdownItem', {
	onClick: 'clicked',
})(StyledDropdownItem);

const DropdownItemComponent = ({
	children,
	onClick,
}: {
	children: ReactNode;
	onClick: (event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
}) => <DropdownItemWithAnalytics onClick={onClick}>{children}</DropdownItemWithAnalytics>;

// visible for testing
export const useIsOpen = () => useState(false);

export const ActivityFeedDropdown = ({ items, selectedItem, onSelectActivityItem }: Props) => {
	const [isDropdownOpen, setIsDropdownOpen] = useIsOpen();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const dropdownTrigger = useCallback(
		({ triggerRef, ...triggerProps }: CustomTriggerProps) => {
			const customThemeButtonProps: ComponentProps<typeof CustomThemeButton> = {
				appearance: 'subtle',
				spacing: 'compact',
				theme: (currentTheme, themeProps) => {
					const { buttonStyles, ...rest } = currentTheme(themeProps);

					const customStyles = {
						padding: `0 ${token('space.050', '4px')} 0 ${token('space.050', '4px')}`,
						margin: `0 ${token('space.050', '4px')} 0 ${token('space.050', '4px')}`,
						backgroundColor: isDropdownOpen
							? token('color.background.selected', colors.N400)
							: token('color.background.neutral', colors.N20),
						'&:hover': {
							backgroundColor: isDropdownOpen
								? token('color.background.selected.hovered', colors.N400)
								: token('color.background.neutral.hovered', colors.N30),
						},
						'&:active': {
							backgroundColor: isDropdownOpen
								? token('color.background.selected.pressed', colors.N400)
								: token('color.background.neutral.pressed', colors.B50),
						},
					};

					return {
						...rest,
						buttonStyles: {
							...buttonStyles,
							...customStyles,
						},
					};
				},
				iconAfter: <ChevronDownIcon label="" />,
			};

			if (selectedItem.type === FORGE_ENTITY_TYPE) {
				return (
					<CustomThemeButton
						ref={triggerRef}
						{...triggerProps}
						{...customThemeButtonProps}
						interactionName="auto-generated.jira-issue-activity-feed.ui.dropdown.button"
					>
						<ForgeIssueActivityTrigger
							item={selectedItem}
							// @ts-expect-error - TS2322 - Type 'FC<any>' is not assignable to type '(props: { children: ReactNode; }) => ReactElement<any, string | JSXElementConstructor<any>>'.
							component={DropdownTrigger}
						/>
					</CustomThemeButton>
				);
			}

			return (
				<CustomThemeButton
					ref={triggerRef}
					{...triggerProps}
					{...customThemeButtonProps}
					interactionName="auto-generated.jira-issue-activity-feed.ui.dropdown.button1"
				>
					<DropdownTrigger>{selectedItem.name}</DropdownTrigger>
				</CustomThemeButton>
			);
		},
		[isDropdownOpen, selectedItem],
	);

	return (
		<ContextualAnalyticsData sourceType={DROPDOWN} sourceName="activityFeed">
			<MountEvent onMount={fireScreenAnalytics} />
			<DropdownMenu
				isOpen={isDropdownOpen}
				testId="issue-activity-feed.ui.dropdown.dropdown-menu-stateless"
				placement="bottom-start"
				trigger={dropdownTrigger}
				onOpenChange={({ isOpen }) => {
					setIsDropdownOpen(isOpen);
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'toggled',
							actionSubject: 'dropdownMenu',
						}),
						{ isOpen },
					);
				}}
			>
				<DropdownItemGroup>
					{items
						.filter((item) => item.key !== selectedItem.key)
						.map((item) => {
							const props = {
								key: item.key,
								onClick: (e: MouseEvent, analyticsEvent: UIAnalyticsEvent) =>
									onSelectActivityItem(item, analyticsEvent, 'dropdownItemSelected', e.timeStamp),
							};

							return item.type === FORGE_ENTITY_TYPE ? (
								<ForgeIssueActivityItem {...props} component={DropdownItemComponent} item={item} />
							) : (
								<DropdownItemComponent {...props}>{item.name}</DropdownItemComponent>
							);
						})}
				</DropdownItemGroup>
			</DropdownMenu>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownTrigger = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${20 * gridSize}px`,
	display: 'inline-block',
	lineHeight: 'normal',
	verticalAlign: 'text-bottom',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	...ellipsis,
});

DropdownTrigger.displayName = 'DropdownTrigger';
